/**
 * UserMessageBubble - Shared user message component
 *
 * Displays user messages with right-aligned styling:
 * - Subtle background (5% foreground)
 * - Pill-shaped corners
 * - Max width 80%
 * - Markdown rendering for links and code
 * - Optional file attachments with thumbnails
 * - Content badges for @mentions (sources, skills)
 * - Pending/queued states (Electron only)
 */

import * as React from 'react'
import type { ReactNode } from 'react'
import type { ContentBadge, MessageInboundContext, StoredAttachment } from '@tentarc/core'
import { normalizePath } from '@tentarc/core/utils'
import { MoreHorizontal, Pencil } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Markdown } from '../markdown'
import { FileTypeIcon, getFileTypeLabel } from './attachment-helpers'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../tooltip'
import { SimpleDropdown, SimpleDropdownItem } from '../ui/SimpleDropdown'

// Fallback text icons for badges without iconDataUrl
// Using simple characters since SVG rendering may not work in all contexts
const SKILL_ICON_TEXT = '✦'
const SOURCE_ICON_TEXT = '⊕'
const CONTEXT_ICON_TEXT = '⚙'
const COMMAND_ICON_TEXT = '/'

/**
 * Check if a badge is an edit_request badge (identified by XML tag in rawText)
 */
function isEditRequestBadge(badge: ContentBadge): boolean {
  return badge.type === 'context' && !!badge.rawText?.includes('<edit_request>')
}

/**
 * EditRequestBadge - Standalone badge rendered above the user message bubble
 * Taller and with larger corner radius than inline badges for visual distinction
 */
function EditRequestBadge({ badge }: { badge: ContentBadge }) {
  const displayLabel = badge.collapsedLabel || badge.label
  return (
    <span className="inline-flex items-center h-[28px] px-2.5 rounded-[8px] bg-background shadow-minimal text-[13px] text-muted-foreground">
      {displayLabel}
    </span>
  )
}

/**
 * UltrathinkBadge - Indicates the message was sent with ultrathink enabled
 * Styled with gradient to match the input area ultrathink badge
 */
function UltrathinkBadge() {
  return (
    <span
      className="inline-flex items-center h-[28px] px-2.5 rounded-[8px] shadow-tinted bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 text-xs font-medium"
      style={{ '--shadow-color': '147, 51, 234' } as React.CSSProperties}
    >
      <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
        Ultrathink
      </span>
    </span>
  )
}

/**
 * InlineBadge - Renders a single content badge inline with text
 * Styled to match the input field badges (bg-background with shadow)
 */
function InlineBadge({ badge }: { badge: ContentBadge }) {
  return (
    <span
      className="inline-flex items-center gap-1 h-[22px] px-1.5 mx-0.5 rounded-[5px] bg-background shadow-minimal text-[12px] align-middle"
      style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
    >
      {badge.iconDataUrl ? (
        <img
          src={badge.iconDataUrl}
          alt=""
          className="h-[12px] w-[12px] rounded-[2px] shrink-0"
        />
      ) : (
        <span className="h-[12px] w-[12px] rounded-[2px] bg-foreground/5 flex items-center justify-center text-foreground/50 shrink-0 text-[8px]">
          {badge.type === 'skill' ? SKILL_ICON_TEXT : badge.type === 'context' ? CONTEXT_ICON_TEXT : SOURCE_ICON_TEXT}
        </span>
      )}
      <span className="truncate max-w-[200px]">{badge.label}</span>
    </span>
  )
}

/**
 * CommandBadge - Renders a slash command badge inline with text
 * Styled similarly to InlineBadge but indicates a SDK command (e.g., /compact)
 */
function CommandBadge({ badge }: { badge: ContentBadge }) {
  return (
    <span
      className="inline-flex items-center gap-1 h-[22px] px-1.5 mx-0.5 rounded-[5px] bg-background shadow-minimal text-[12px] align-middle"
      style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
    >
      <span className="h-[12px] w-[12px] rounded-[2px] bg-foreground/5 flex items-center justify-center text-foreground/50 shrink-0 text-[10px] font-medium">
        {COMMAND_ICON_TEXT}
      </span>
      <span className="truncate max-w-[200px]">{badge.label}</span>
    </span>
  )
}

/**
 * ContextBadge - Renders a context badge that collapses hidden content
 * Shows collapsed label and hides the raw content from display
 * Note: edit_request badges are handled separately by EditRequestBadge
 */
function ContextBadge({ badge }: { badge: ContentBadge }) {
  const displayLabel = badge.collapsedLabel || badge.label

  return (
    <span
      className="inline-flex items-center gap-1 h-[22px] px-1.5 mr-1 rounded-[5px] bg-background shadow-minimal text-[12px] align-middle"
      style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
      title="Context badge"
    >
      <span className="h-[12px] w-[12px] rounded-[2px] bg-foreground/5 flex items-center justify-center text-foreground/50 shrink-0 text-[8px]">
        {CONTEXT_ICON_TEXT}
      </span>
      <span className="truncate max-w-[200px] text-muted-foreground">{displayLabel}</span>
    </span>
  )
}

/** Known code file extensions for picking the code file icon */
const CODE_EXTENSIONS = new Set([
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs',
  'py', 'rs', 'go', 'java', 'rb', 'swift', 'kt',
  'c', 'cpp', 'h', 'hpp', 'cs',
  'css', 'scss', 'less', 'html', 'vue', 'svelte',
  'json', 'yaml', 'yml', 'toml', 'xml',
  'sh', 'bash', 'zsh', 'fish',
  'md', 'mdx',
  'sql', 'graphql', 'proto',
])

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'svg'])
const AUDIO_EXTENSIONS = new Set(['mp3', 'ogg', 'oga', 'wav', 'm4a', 'aac', 'flac', 'opus'])

// ============================================================================
// Custom Audio Player — bypasses native <audio controls> shadow DOM hit-test
// issues inside Electron + Radix ScrollArea environments.
// ============================================================================

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="ml-0.5">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  )
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
    </svg>
  )
}

interface AudioAttachmentCardProps {
  /** storedPath to load audio from via resolveDataUrl */
  storedPath?: string
  /** Resolver that reads local file and returns blob/data URL */
  resolveDataUrl?: (storedPath: string, mimeType?: string) => Promise<string | null>
  /** Fallback URL (e.g. file://) if blob loading fails */
  fallbackSrc?: string | null
  fileName: string
  fileMimeType: string
}

function AudioAttachmentCard({
  storedPath,
  resolveDataUrl,
  fallbackSrc,
  fileName,
  fileMimeType,
}: AudioAttachmentCardProps) {
  const audioRef = React.useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = React.useState(false)
  const [progress, setProgress] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [error, setError] = React.useState(false)
  const [audioSrc, setAudioSrc] = React.useState<string | undefined>(undefined)

  // Self-load: resolve blob URL from storedPath
  React.useEffect(() => {
    if (!storedPath || !resolveDataUrl) {
      // No way to load — fall back to file:// URL immediately
      if (fallbackSrc) setAudioSrc(fallbackSrc)
      return
    }
    let cancelled = false

    resolveDataUrl(storedPath, fileMimeType)
      .then((url) => {
        if (cancelled) return
        if (url) {
          setAudioSrc(url)
        } else if (fallbackSrc) {
          setAudioSrc(fallbackSrc)
        } else {
          setError(true)
        }
      })
      .catch(() => {
        if (cancelled) return
        if (fallbackSrc) {
          setAudioSrc(fallbackSrc)
        } else {
          setError(true)
        }
      })

    return () => { cancelled = true }
  }, [storedPath, resolveDataUrl, fallbackSrc, fileMimeType])

  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onEnded = () => { setPlaying(false); setProgress(0); setCurrentTime(0) }
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
      if (audio.duration > 0) setProgress(audio.currentTime / audio.duration)
    }
    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration)
      debugLogInHost('[AudioAttachmentCard] metadata loaded', {
        fileName,
        duration: audio.duration,
        srcType: audioSrc?.startsWith('blob:') ? 'blob' : audioSrc?.startsWith('file:') ? 'file' : 'other',
      })
    }
    const onError = () => {
      setPlaying(false)
      setError(true)
      const mediaErr = audio.error
      debugLogInHost('[AudioAttachmentCard] playback error', {
        fileName,
        fileMimeType,
        srcType: audioSrc?.startsWith('blob:') ? 'blob' : audioSrc?.startsWith('file:') ? 'file' : 'other',
        code: mediaErr?.code ?? null,
        message: mediaErr?.message ?? null,
      })
    }

    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('error', onError)

    return () => {
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('error', onError)
    }
  }, [fileName, fileMimeType, audioSrc])

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      audio.play().catch(() => setError(true))
    } else {
      audio.pause()
    }
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const audio = audioRef.current
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * audio.duration
  }

  return (
    <div
      className="w-[280px] max-w-full rounded-[10px] bg-foreground/5 px-3 py-2 titlebar-no-drag"
      style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
    >
      {/* Hidden audio element — only mount when we have a source */}
      {audioSrc && <audio ref={audioRef} preload="metadata" src={audioSrc} />}

      {/* Custom controls or loading state */}
      {audioSrc ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-7 h-7 rounded-full bg-foreground/10 hover:bg-foreground/20 flex items-center justify-center shrink-0 transition-colors cursor-pointer"
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>

          <div className="flex-1 min-w-0">
            <div
              className="h-1.5 rounded-full bg-foreground/10 cursor-pointer relative"
              onClick={handleSeek}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div
                className="h-full rounded-full bg-foreground/40 transition-[width] duration-75"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5 tabular-nums select-none">
              <span>{formatAudioTime(currentTime)}</span>
              <span>{duration > 0 ? formatAudioTime(duration) : '--:--'}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center shrink-0 opacity-40">
            <PlayIcon />
          </div>
          <div className="flex-1 min-w-0">
            <div className="h-1.5 rounded-full bg-foreground/10" />
            <div className="text-[10px] text-muted-foreground mt-0.5 select-none">Loading…</div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="text-[10px] text-muted-foreground/60 mt-1">
          Unable to play audio
        </div>
      )}
    </div>
  )
}
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'mkv'])

type AttachmentRenderKind = 'image' | 'audio' | 'video' | 'file'

function attachmentPreviewKey(att: StoredAttachment, index: number): string {
  return att.id || att.storedPath || `${att.name}-${index}`
}

function fileExtension(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase()
  return ext ?? ''
}

function inferAttachmentRenderKind(att: StoredAttachment): AttachmentRenderKind {
  const mime = att.mimeType?.toLowerCase() ?? ''
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('video/')) return 'video'

  // Some integrations only provide application/octet-stream; infer from file extension.
  const ext = fileExtension(att.name)
  if (IMAGE_EXTENSIONS.has(ext)) return 'image'
  if (AUDIO_EXTENSIONS.has(ext)) return 'audio'
  if (VIDEO_EXTENSIONS.has(ext)) return 'video'

  if (att.type === 'image') return 'image'
  return 'file'
}

function toLocalFileUrl(storedPath?: string): string | null {
  if (!storedPath) return null
  if (storedPath.startsWith('file://')) return storedPath

  const normalized = storedPath.replace(/\\/g, '/')
  if (/^[a-zA-Z]:\//.test(normalized)) {
    // Windows absolute path: C:/...
    return `file:///${encodeURI(normalized)}`
  }
  if (normalized.startsWith('/')) {
    // POSIX absolute path: /Users/...
    return `file://${encodeURI(normalized)}`
  }
  return null
}

function revokeObjectUrlIfNeeded(url?: string): void {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

function debugLogInHost(...args: unknown[]): void {
  const hostWindow = window as unknown as {
    electronAPI?: {
      debugLog?: (...logArgs: unknown[]) => void
    }
  }
  hostWindow.electronAPI?.debugLog?.(...args)
}

// Strip synthetic integration scaffolding and keep user-meaningful text only.
function stripIntegrationAttachmentHints(text: string): string {
  let stripped = text.replace(
    /^\[[^\]\n]+\]\s+Received\s+\d+\s+attachment\(s\)(?:\s+\([^)]+\))?\.\s+Files are attached to this message\.\s*$/gim,
    ''
  )
  // Strip synthetic audio transcription header + bullet lines entirely so the user
  // only sees their original text (if any), not the STT scaffolding.
  stripped = stripped.replace(/^\[[^\]\n]+\s+audio transcription\]\s*$/gim, '')
  stripped = stripped.replace(/^\s*[-*]\s*#\d+\s+clip#\d+\s+[^:]+:\s*/gim, '')
  stripped = stripped.replace(/^\s*[-*]\s*#\d+\s+.*$/gim, '')
  return stripped.replace(/\n{3,}/g, '\n\n').trim()
}

/** Returns the appropriate file/folder SVG icon based on badge type and file extension */
function FileBadgeIcon({ badge }: { badge: ContentBadge }) {
  if (badge.type === 'folder') {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" className="shrink-0 text-muted-foreground">
        <path d="M20.5 10C20.5 9.07003 20.5 8.60504 20.3978 8.22354C20.1204 7.18827 19.3117 6.37962 18.2765 6.10222C17.895 6 17.43 6 16.5 6H13.1008C12.4742 6 12.1609 6 11.8739 5.91181C11.6824 5.85298 11.5009 5.76572 11.3353 5.65295C11.0871 5.48389 10.8914 5.23926 10.5 4.75L10.4095 4.63693C10.107 4.25881 9.9558 4.06975 9.7736 3.92674C9.54464 3.74703 9.27921 3.61946 8.99585 3.55294C8.77037 3.5 8.52825 3.5 8.04402 3.5C6.60485 3.5 5.88527 3.5 5.32008 3.74178C4.61056 4.0453 4.0453 4.61056 3.74178 5.32008C3.5 5.88527 3.5 6.60485 3.5 8.04402V10M9.46502 20.5H14.535C16.9102 20.5 18.0978 20.5 18.9301 19.8113C19.7624 19.1226 19.9846 17.9559 20.429 15.6227L20.8217 13.5613C21.1358 11.9121 21.2929 11.0874 20.843 10.5437C20.393 10 19.5536 10 17.8746 10H6.12537C4.44643 10 3.60696 10 3.15704 10.5437C2.70713 11.0874 2.8642 11.9121 3.17835 13.5613L3.57099 15.6227C4.01541 17.9559 4.23763 19.1226 5.06992 19.8113C5.90221 20.5 7.08981 20.5 9.46502 20.5Z"/>
      </svg>
    )
  }

  // Check if it's a code file
  const ext = badge.label.split('.').pop()?.toLowerCase()
  const isCode = ext ? CODE_EXTENSIONS.has(ext) : false

  if (isCode) {
    // Code file icon (document with < > brackets)
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted-foreground">
        <path d="M10.5 2.5C12.1569 2.5 13.5 3.84315 13.5 5.5V6.1C13.5 6.4716 13.5 6.6574 13.5246 6.81287C13.6602 7.66865 14.3313 8.33983 15.1871 8.47538C15.3426 8.5 15.5284 8.5 15.9 8.5H16.5C18.1569 8.5 19.5 9.84315 19.5 11.5M10.5 12.8799C9.70024 13.2985 9.10807 13.8275 8.64232 14.5478C8.51063 14.7515 8.44479 14.8533 8.44489 15.0011C8.44498 15.1488 8.51099 15.2506 8.643 15.4542C9.1095 16.1736 9.70167 16.7028 10.5 17.1225M13.5 12.8799C14.2998 13.2985 14.8919 13.8275 15.3577 14.5478C15.4894 14.7515 15.5552 14.8533 15.5551 15.0011C15.555 15.1488 15.489 15.2506 15.357 15.4542C14.8905 16.1736 14.2983 16.7028 13.5 17.1225M10.9645 2.5H10.6678C8.64635 2.5 7.63561 2.5 6.84835 2.85692C5.96507 3.25736 5.25736 3.96507 4.85692 4.84835C4.5 5.63561 4.5 6.64635 4.5 8.66781V14C4.5 17.2875 4.5 18.9312 5.40796 20.0376C5.57418 20.2401 5.75989 20.4258 5.96243 20.592C7.06878 21.5 8.71252 21.5 12 21.5C15.2875 21.5 16.9312 21.5 18.0376 20.592C18.2401 20.4258 18.4258 20.2401 18.592 20.0376C19.5 18.9312 19.5 17.2875 19.5 14V11.0355C19.5 10.0027 19.5 9.48628 19.4176 8.99414C19.2671 8.09576 18.9141 7.24342 18.3852 6.50177C18.0955 6.09549 17.7303 5.73032 17 5C16.2697 4.26968 15.9045 3.90451 15.4982 3.6148C14.7566 3.08595 13.9042 2.7329 13.0059 2.58243C12.5137 2.5 11.9973 2.5 10.9645 2.5Z"/>
      </svg>
    )
  }

  // Generic file icon (document with folded corner)
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted-foreground">
      <path d="M10.5 2.5C12.1569 2.5 13.5 3.84315 13.5 5.5V6.1C13.5 6.4716 13.5 6.6574 13.5246 6.81287C13.6602 7.66865 14.3313 8.33983 15.1871 8.47538C15.3426 8.5 15.5284 8.5 15.9 8.5H16.5C18.1569 8.5 19.5 9.84315 19.5 11.5M9 16H15M9 12H10M10.9645 2.5H10.6678C8.64635 2.5 7.63561 2.5 6.84835 2.85692C5.96507 3.25736 5.25736 3.96507 4.85692 4.84835C4.5 5.63561 4.5 6.64635 4.5 8.66781V14C4.5 17.2875 4.5 18.9312 5.40796 20.0376C5.57418 20.2401 5.75989 20.4258 5.96243 20.592C7.06878 21.5 8.71252 21.5 12 21.5C15.2875 21.5 16.9312 21.5 18.0376 20.592C18.2401 20.4258 18.4258 20.2401 18.592 20.0376C19.5 18.9312 19.5 17.2875 19.5 14V11.0355C19.5 10.0027 19.5 9.48628 19.4176 8.99414C19.2671 8.09576 18.9141 7.24342 18.3852 6.50177C18.0955 6.09549 17.7303 5.73032 17 5C16.2697 4.26968 15.9045 3.90451 15.4982 3.6148C14.7566 3.08595 13.9042 2.7329 13.0059 2.58243C12.5137 2.5 11.9973 2.5 10.9645 2.5Z"/>
    </svg>
  )
}

/**
 * InlineFileBadge - File/folder badge for inline display within text.
 * Shows proper icon (folder, code file, or generic file) with Tooltip for full path.
 * Optionally clickable when onFileClick is provided.
 */
function InlineFileBadge({
  badge,
  onFileClick
}: {
  badge: ContentBadge
  onFileClick?: (path: string) => void
}) {
  // Strip .Tentarcspace/session path prefix for cleaner tooltip display
  // e.g. "/Users/.../workspaces/{id}/sessions/{id}/plans/foo.md" → "plans/foo.md"
  const rawPath = badge.filePath || badge.label
  const tooltipPath = normalizePath(rawPath).replace(/^.*\.tentarc\/workspaces\/[^/]+\/(sessions\/[^/]+\/)?/, '')
  const isClickable = !!badge.filePath && !!onFileClick

  const badgeContent = (
    <span
      role={isClickable ? 'button' : undefined}
      onClick={() => isClickable && onFileClick!(badge.filePath!)}
      className={cn(
        "inline-flex items-center gap-1 h-[22px] px-1.5 mx-0.5 rounded-[5px] bg-background shadow-minimal text-[12px] align-middle",
        isClickable && "hover:bg-foreground/5 transition-colors cursor-pointer"
      )}
      style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
    >
      <FileBadgeIcon badge={badge} />
      <span className="truncate max-w-[200px]">{badge.label}</span>
    </span>
  )

  // Wrap with Tooltip to show full path on hover
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeContent}
        </TooltipTrigger>
        <TooltipContent side="top">
          {tooltipPath}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * Render content with badges inserted at their positions.
 * Text segments between badges are rendered as Markdown.
 *
 * Context badges (type='context') are special:
 * - They completely hide the marked content range
 * - They show a collapsed badge with the collapsedLabel
 * - Used for EditPopover metadata that shouldn't be visible to users
 *
 * File badges (type='file') render inline as clickable badges:
 * - Used for plan execution messages where file path appears inline with text
 */
function renderContentWithBadges(
  content: string,
  badges: ContentBadge[],
  onUrlClick?: (url: string) => void,
  onFileClick?: (path: string) => void
): ReactNode {
  if (badges.length === 0) {
    return (
      <Markdown
        mode="minimal"
        onUrlClick={onUrlClick}
        onFileClick={onFileClick}
        className="text-sm [&_a]:underline [&_code]:bg-foreground/10"
      >
        {content}
      </Markdown>
    )
  }

  // Sort badges by start position
  const sortedBadges = [...badges].sort((a, b) => a.start - b.start)

  const elements: ReactNode[] = []
  let lastEnd = 0

  sortedBadges.forEach((badge, i) => {
    // Add text before this badge
    if (badge.start > lastEnd) {
      const textBefore = content.slice(lastEnd, badge.start)
      if (textBefore.trim()) {
        elements.push(
          <span key={`text-${i}`} className="whitespace-pre-wrap">
            {textBefore}
          </span>
        )
      }
    }

    // Context badges hide content and show collapsed label
    // Command badges show SDK commands like /compact
    // File badges show clickable file references inline
    // Source/skill badges show inline with the original text
    // Note: edit_request badges are filtered out and rendered above the bubble separately
    if (badge.type === 'context') {
      elements.push(<ContextBadge key={`badge-${i}`} badge={badge} />)
    } else if (badge.type === 'command') {
      elements.push(<CommandBadge key={`badge-${i}`} badge={badge} />)
    } else if (badge.type === 'file' || badge.type === 'folder') {
      elements.push(<InlineFileBadge key={`badge-${i}`} badge={badge} onFileClick={onFileClick} />)
    } else {
      elements.push(<InlineBadge key={`badge-${i}`} badge={badge} />)
    }

    lastEnd = badge.end
  })

  // Add remaining text after last badge
  if (lastEnd < content.length) {
    const textAfter = content.slice(lastEnd)
    if (textAfter.trim()) {
      elements.push(
        <span key="text-end" className="whitespace-pre-wrap">
          {textAfter}
        </span>
      )
    }
  }

  // Use <p> to match Markdown's block-level line-height behavior
  return <p className="text-sm">{elements}</p>
}

export interface UserMessageBubbleProps {
  /** Message content (markdown supported) */
  content: string
  /** Structured inbound context for platform-originated user messages */
  inboundContext?: MessageInboundContext
  /** Additional className for the outer container */
  className?: string
  /** Callback when a URL is clicked */
  onUrlClick?: (url: string) => void
  /** Callback when a file path is clicked */
  onFileClick?: (path: string) => void
  /** Stored attachments (images, documents) */
  attachments?: StoredAttachment[]
  /** Optional loader for rendering media previews from local files */
  resolveAttachmentDataUrl?: (storedPath: string, mimeType?: string) => Promise<string | null>
  /** Content badges for inline display (sources, skills) */
  badges?: ContentBadge[]
  /** Whether the message is pending (shimmer animation) */
  isPending?: boolean
  /** Whether the message is queued (badge shown) */
  isQueued?: boolean
  /** Whether the message was sent with ultrathink enabled */
  ultrathink?: boolean
  /** Label for queued badge (defaults to "queued") */
  queuedLabel?: string
  /** Optional queued-message action controls */
  queuedActions?: {
    primaryLabel: string
    onPrimary: () => void
    editLabel: string
    onEdit: () => void
    queueingEnabled: boolean
    turnOffQueueingLabel: string
    turnOnQueueingLabel: string
    onToggleQueueing: () => void
  }
}

export function UserMessageBubble({
  content,
  inboundContext,
  className,
  onUrlClick,
  onFileClick,
  attachments,
  resolveAttachmentDataUrl,
  badges,
  isPending,
  isQueued,
  ultrathink,
  queuedLabel = 'queued',
  queuedActions,
}: UserMessageBubbleProps) {
  const hasAttachments = attachments && attachments.length > 0
  const [attachmentDataUrls, setAttachmentDataUrls] = React.useState<Record<string, string>>({})
  const attachmentDataUrlsRef = React.useRef<Record<string, string>>({})
  const pendingPreviewKeysRef = React.useRef<Set<string>>(new Set())

  React.useEffect(() => {
    attachmentDataUrlsRef.current = attachmentDataUrls
  }, [attachmentDataUrls])

  // Keep preview cache aligned with current attachment list.
  React.useEffect(() => {
    if (!attachments?.length) {
      for (const url of Object.values(attachmentDataUrlsRef.current)) {
        revokeObjectUrlIfNeeded(url)
      }
      setAttachmentDataUrls({})
      pendingPreviewKeysRef.current.clear()
      return
    }

    const activeKeys = new Set(attachments.map((att, index) => attachmentPreviewKey(att, index)))
    setAttachmentDataUrls((prev) => {
      let changed = false
      const next: Record<string, string> = {}
      for (const [key, value] of Object.entries(prev)) {
        if (activeKeys.has(key)) {
          next[key] = value
        } else {
          revokeObjectUrlIfNeeded(value)
          changed = true
        }
      }
      return changed ? next : prev
    })

    for (const key of Array.from(pendingPreviewKeysRef.current)) {
      if (!activeKeys.has(key)) {
        pendingPreviewKeysRef.current.delete(key)
      }
    }
  }, [attachments])

  // Revoke preview blob URLs on unmount to avoid memory leaks.
  React.useEffect(() => {
    return () => {
      for (const url of Object.values(attachmentDataUrlsRef.current)) {
        revokeObjectUrlIfNeeded(url)
      }
    }
  }, [])

  // Load preview data URLs for media attachments (audio/video/image without thumbnail).
  React.useEffect(() => {
    if (!attachments?.length || !resolveAttachmentDataUrl) return
    let cancelled = false

    const loadMediaPreviews = async () => {
      for (let index = 0; index < attachments.length; index += 1) {
        const attachment = attachments[index]
        if (!attachment) continue
        if (!attachment.storedPath) continue

        const kind = inferAttachmentRenderKind(attachment)
        const needsDataUrl =
          kind === 'audio' ||
          kind === 'video' ||
          (kind === 'image' && !attachment.thumbnailBase64)
        if (!needsDataUrl) continue

        const key = attachmentPreviewKey(attachment, index)
        if (attachmentDataUrlsRef.current[key] || pendingPreviewKeysRef.current.has(key)) continue

        pendingPreviewKeysRef.current.add(key)
        try {
          const dataUrl = await resolveAttachmentDataUrl(attachment.storedPath, attachment.mimeType)
          if (!cancelled && dataUrl) {
            setAttachmentDataUrls((prev) => {
              if (prev[key]) return prev
              return { ...prev, [key]: dataUrl }
            })
          }
        } catch {
          // Preview is optional - fallback card remains usable.
        } finally {
          pendingPreviewKeysRef.current.delete(key)
        }
      }
    }

    void loadMediaPreviews()
    return () => {
      cancelled = true
    }
  }, [attachments, resolveAttachmentDataUrl])

  // Separate edit_request badges (rendered above bubble) from other badges (rendered inline)
  const editRequestBadges = badges?.filter(isEditRequestBadge) ?? []
  const inlineBadges = badges?.filter(b => !isEditRequestBadge(b)) ?? []
  const hasEditRequestBadges = editRequestBadges.length > 0
  const hasInlineBadges = inlineBadges.length > 0

  // Strip edit_request content from the displayed text
  // Each badge has start/end positions marking where to remove content
  let displayContent = content
  if (hasEditRequestBadges) {
    // Sort badges by start position descending so we can remove from end to start
    // (this preserves positions for earlier removals)
    const sortedBadges = [...editRequestBadges].sort((a, b) => b.start - a.start)
    for (const badge of sortedBadges) {
      displayContent = displayContent.slice(0, badge.start) + displayContent.slice(badge.end)
    }
    displayContent = displayContent.trim()
  }
  displayContent = stripIntegrationAttachmentHints(displayContent)
  const hasTextContent = displayContent.length > 0 || hasInlineBadges
  const senderLabel = getSenderLabel(inboundContext)

  return (
    <div className={cn("flex flex-col items-end gap-3 w-full", className)}>
      {senderLabel && (
        <div className="max-w-[80%] px-1 text-[12px] font-medium text-muted-foreground text-right">
          {senderLabel}
        </div>
      )}

      {/* Attachment preview row - stored attachments with thumbnails */}
      {hasAttachments && (
        <div className="flex gap-2 justify-end max-w-[80%] flex-wrap">
          {attachments!.map((att, i) => {
            const key = attachmentPreviewKey(att, i)
            const kind = inferAttachmentRenderKind(att)
            const previewDataUrl = attachmentDataUrls[key]
            const localFileUrl = toLocalFileUrl(att.storedPath)
            const imageSrc = att.thumbnailBase64
              ? `data:image/png;base64,${att.thumbnailBase64}`
              : previewDataUrl

            if (kind === 'image') {
              return (
                <div
                  key={key}
                  className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => att.storedPath && onFileClick?.(att.storedPath)}
                  title={`Click to open ${att.name}`}
                >
                  <div className="h-14 w-14 rounded-[8px] overflow-hidden bg-background shadow-minimal">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={att.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <FileTypeIcon type={att.type} mimeType={att.mimeType} className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                </div>
              )
            }

            if (kind === 'audio') {
              return (
                <AudioAttachmentCard
                  key={key}
                  storedPath={att.storedPath}
                  resolveDataUrl={resolveAttachmentDataUrl}
                  fallbackSrc={localFileUrl}
                  fileName={att.name}
                  fileMimeType={att.mimeType}
                />
              )
            }

            if (kind === 'video') {
              const videoSrc = previewDataUrl || localFileUrl
              return (
                <div key={key} className="w-[280px] max-w-full rounded-[10px] bg-foreground/5 p-2 pointer-events-auto">
                  {videoSrc ? (
                    <video
                      controls
                      preload="metadata"
                      className="h-[150px] w-full rounded-[8px] bg-black object-contain pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <source src={videoSrc} type={att.mimeType || undefined} />
                    </video>
                  ) : (
                    <div className="h-[150px] w-full rounded-[8px] bg-background shadow-minimal flex items-center justify-center">
                      <FileTypeIcon type={att.type} mimeType={att.mimeType} className="h-6 w-6" />
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium truncate" title={att.name}>
                      {att.name}
                    </span>
                    {att.storedPath && (
                      <button
                        type="button"
                        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0 pointer-events-auto cursor-pointer underline decoration-dotted underline-offset-2"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onFileClick?.(att.storedPath)
                        }}
                      >
                        Open
                      </button>
                    )}
                  </div>
                </div>
              )
            }

            return (
              <div
                key={key}
                className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => att.storedPath && onFileClick?.(att.storedPath)}
                title={`Click to open ${att.name}`}
              >
                <div className="flex items-center gap-2.5 rounded-[8px] bg-foreground/5 pl-1.5 pr-3 py-1.5">
                  <div className="h-11 w-8 rounded-[6px] overflow-hidden bg-background shadow-minimal flex items-center justify-center shrink-0">
                    {att.thumbnailBase64 ? (
                      <img
                        src={`data:image/png;base64,${att.thumbnailBase64}`}
                        alt={att.name}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <FileTypeIcon type={att.type} mimeType={att.mimeType} className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex flex-col min-w-0 max-w-[120px]">
                    <span className="text-xs font-medium line-clamp-2 break-all" title={att.name}>
                      {att.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {getFileTypeLabel(att.type, att.mimeType, att.name)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Badges row - ultrathink and edit request badges above text bubble */}
      {(ultrathink || hasEditRequestBadges) && (
        <div className="flex gap-2 justify-end max-w-[80%] flex-wrap">
          {ultrathink && <UltrathinkBadge />}
          {editRequestBadges.map((badge, i) => (
            <EditRequestBadge key={`edit-badge-${i}`} badge={badge} />
          ))}
        </div>
      )}

      {/* Text content bubble */}
      {hasTextContent && (
        <div
          className={cn(
            "max-w-[80%] bg-foreground/5 rounded-[16px] px-5 py-3.5 break-words min-w-0 select-text [&_p]:m-0",
            isPending && "animate-shimmer"
          )}
        >
          {hasInlineBadges
            ? renderContentWithBadges(displayContent, inlineBadges, onUrlClick, onFileClick)
            : (
              <Markdown
                mode="minimal"
                onUrlClick={onUrlClick}
                onFileClick={onFileClick}
                className="text-sm [&_a]:underline [&_code]:bg-foreground/10"
              >
                {displayContent}
              </Markdown>
            )
          }
        </div>
      )}

      {/* Queued controls + badge */}
      {isQueued && (
        <div className="flex items-center gap-1">
          {queuedActions && (
            <>
              <button
                type="button"
                className="inline-flex items-center h-7 px-3 rounded-full text-[13px] bg-foreground/5 hover:bg-foreground/10 transition-colors"
                onClick={queuedActions.onPrimary}
              >
                {queuedActions.primaryLabel}
              </button>
              <SimpleDropdown
                align="end"
                trigger={(
                  <div
                    role="button"
                    tabIndex={0}
                    className="h-7 w-7 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                      }
                    }}
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </div>
                )}
              >
                <SimpleDropdownItem
                  onClick={queuedActions.onEdit}
                  icon={<Pencil />}
                >
                  {queuedActions.editLabel}
                </SimpleDropdownItem>
                <SimpleDropdownItem
                  onClick={queuedActions.onToggleQueueing}
                >
                  {queuedActions.queueingEnabled
                    ? queuedActions.turnOffQueueingLabel
                    : queuedActions.turnOnQueueingLabel}
                </SimpleDropdownItem>
              </SimpleDropdown>
            </>
          )}
          <span className="text-[10px] text-muted-foreground bg-foreground/5 px-2 py-0.5 rounded-full">
            {queuedLabel}
          </span>
        </div>
      )}
    </div>
  )
}

function getSenderLabel(inboundContext?: MessageInboundContext): string | null {
  if (!inboundContext) return null
  if (inboundContext.chatType === 'p2p') return null
  const sender = inboundContext.sender
  if (!sender) return null
  return sender.name?.trim() || sender.id.trim() || null
}
